/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentProps } from 'preact';
import type { Dispatch, StateUpdater } from 'preact/hooks';
import type { FocusableHandle } from '#hooks/UNSAFE_useFocusableTextField';
import type { SelectableHandle } from '#hooks/UNSAFE_useSelectableTextField';
import type { BundleType } from '#resources/nls/bundle';
import type { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import type {
  CellContextMenuContext,
  ColumnResizeDetail,
  SortCriterionDetail,
  TableContextMenuConfig
} from '#UNSAFE_TableView';
import type { AppliedColumnWidths } from './Table';

import { useCallback, useRef, useState } from 'preact/hooks';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { Button } from '#UNSAFE_Button';
import { Dialog } from '#UNSAFE_Dialog';
import { Flex } from '#UNSAFE_Flex';
import { Menu, MenuItem, SelectMenuItem, SelectSingleMenuGroup, Submenu } from '#UNSAFE_Menu';
import { NumberInputText } from '#UNSAFE_NumberInputText';

type Props<K extends string | number, D, C extends string> = {
  contextMenuConfig: TableContextMenuConfig<K, D, C>;
  contextMenuContext: CellContextMenuContext<K, D, C>;
} & Omit<ComponentProps<typeof Menu>, 'children'>;

/**
 * The internal component used to render a custom context menu on the Table
 */
export function TableContextMenu<K extends string | number, D, C extends string>({
  contextMenuConfig,
  contextMenuContext,
  ...menuProps
}: Props<K, D, C>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const accessibleLabel =
    contextMenuConfig.accessibleLabel != null
      ? contextMenuConfig.accessibleLabel
      : translations.accContextMenuLabel();

  return contextMenuConfig.itemsRenderer(contextMenuContext) != null ? (
    <Menu aria-label={accessibleLabel} {...menuProps}>
      {menuProps.isOpen && contextMenuConfig.itemsRenderer(contextMenuContext)}
    </Menu>
  ) : (
    <></>
  );
}

type SortItemProps<C extends string> = {
  columnKey: C;
  isDisabled: boolean;
  currentSortDirection?: 'ascending' | 'descending';
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void;
};

/**
 * The internal component used to render the context menu sort item on the Table
 */
export function TableContextMenuSort<C extends string>({
  columnKey,
  isDisabled,
  currentSortDirection,
  onSortCriterionChange
}: SortItemProps<C>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const _handleSort: Required<ComponentProps<typeof SelectSingleMenuGroup>>['onCommit'] = ({
    value
  }) => {
    if (onSortCriterionChange != null && value !== currentSortDirection) {
      onSortCriterionChange([
        { key: columnKey, sortDirection: value === 'ascending' ? 'ascending' : 'descending' }
      ]);
    }
  };

  return (
    <Submenu label={translations.collection_sortColumnMenu()}>
      <SelectSingleMenuGroup value={currentSortDirection} onCommit={_handleSort}>
        <SelectMenuItem
          isDisabled={isDisabled}
          value="ascending"
          label={translations.collection_sortAscendingSubMenu()}
        />
        <SelectMenuItem
          isDisabled={isDisabled}
          value="descending"
          label={translations.collection_sortDescendingSubMenu()}
        />
      </SelectSingleMenuGroup>
    </Submenu>
  );
}

type ResizeItemProps = {
  isDisabled: boolean;
  setIsResizeDialogOpen: Dispatch<StateUpdater<boolean>>;
};

/**
 * The internal component used to render the context menu column resize item on the Table
 */
export function TableContextMenuResize({ isDisabled, setIsResizeDialogOpen }: ResizeItemProps) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const handleResize = () => {
    setIsResizeDialogOpen(true);
  };

  return (
    <MenuItem
      label={translations.collection_resizeColumnMenu()}
      onAction={handleResize}
      isDisabled={isDisabled}
    />
  );
}

type ResizeDialogProps<C extends string> = {
  isOpen: boolean;
  columnKey: C;
  onColumnResize?: (detail: ColumnResizeDetail<C>) => void;
  appliedColumnWidths: AppliedColumnWidths<C>;
  setIsResizeDialogOpen: Dispatch<StateUpdater<boolean>>;
};

/**
 * The internal component used to render the column resize dialog on the Table
 */
export function TableResizeColumnDialog<C extends string>({
  isOpen,
  columnKey,
  onColumnResize,
  appliedColumnWidths,
  setIsResizeDialogOpen
}: ResizeDialogProps<C>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const initialWidth = Math.round(appliedColumnWidths.netColumnWidths[columnKey]);
  const initialWidthRef = useRef<number>(initialWidth);

  const numberInputRef = useRef<FocusableHandle & SelectableHandle>(null);

  const [widthValue, setWidthValue] = useState<string | undefined>(String(initialWidth));

  let isDisabled;
  let messages: ComponentProps<typeof NumberInputText>['messages'];
  if (
    widthValue == null ||
    Number.isNaN(parseInt(widthValue)) ||
    String(parseInt(widthValue)) !== widthValue.trim() ||
    parseInt(widthValue) < 16 ||
    parseInt(widthValue) > 100000
  ) {
    messages = [
      {
        severity: 'error',
        detail: translations.collection_resizeMessageDetail({ MIN: '16', MAX: '100000' })
      }
    ];
    isDisabled = true;
  } else {
    isDisabled = false;
  }

  const apply = useCallback(() => {
    if (columnKey != null && onColumnResize != null && widthValue != null) {
      if (parseInt(widthValue) !== initialWidthRef.current) {
        onColumnResize({
          key: columnKey,
          newWidth: parseInt(widthValue),
          allColumnWidths: appliedColumnWidths.netColumnWidths,
          paddingWidth: appliedColumnWidths.totalBorderOffset,
          gesture: 'contextMenu',
          type: 'absolute'
        });
      }
    }
    setIsResizeDialogOpen(false);
  }, [columnKey, onColumnResize, widthValue, appliedColumnWidths, setIsResizeDialogOpen]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !isDisabled) {
        apply();
      }
    },
    [apply, isDisabled]
  );

  const cancel = useCallback(() => {
    setIsResizeDialogOpen(false);
  }, [setIsResizeDialogOpen]);

  const handleOnInput = useCallback((detail: ValueUpdateDetail<string>) => {
    setWidthValue(detail.value);
  }, []);

  const footer = (
    <Flex gap="2x">
      <Button onAction={cancel} label={translations.collection_cancel()} />
      <Button
        variant="callToAction"
        onAction={apply}
        label={translations.collection_apply()}
        isDisabled={isDisabled}
      />
    </Flex>
  );

  return (
    <Dialog
      autoFocusRef={numberInputRef}
      isOpen={isOpen}
      modality={'modal'}
      header={translations.collection_resizeColumnDialog()}
      footer={footer}>
      <div onKeyDown={handleKeyDown}>
        <NumberInputText
          label={translations.collection_width()}
          onInput={handleOnInput}
          suffix={translations.collection_pixels()}
          messages={messages}
          isRequired={true}
          isRequiredShown={true}
          ref={numberInputRef}
          value={widthValue}
        />
      </div>
    </Dialog>
  );
}
